import {
  dismissError,
  setWorkInProgress,
  reportError,
  setVisible,
  reportConfigurationNotFoundError,
} from '@configuratorware/configurator-frontendgui/App/Reducers/UI/Actions';
import * as apiActions from '@configuratorware/configurator-frontendgui/App/Reducers/Actions';
import {
  applyVariantAmountPreSelection,
  deleteMissingVariants,
  doCalculation,
  fetchConfigurationVariants,
  fetchInspirations,
  inspectCheckResults,
  inspectValidationResults,
  invalidateCalculation,
  loadInitialOptionclassification,
  RECEIVE_CONFIGURATION,
  receiveDesignerData,
  setCalculationEnabled,
  setDefaultSelectedVariantIdentifier,
  setLoadCode,
} from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { getDesignData } from '@configuratorware/configurator-frontendgui/App/Reducers/DesignData/Reducer';
import {
  getConfiguration,
  getConfigurator,
} from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Selectors';
import { selectConfigurationMode } from '@configuratorware/configurator-frontendgui/App/Reducers/ConfigurationMode/Actions';
import { getAvailableConfigurationModes } from '@configuratorware/configurator-frontendgui/App/Reducers/ConfigurationMode/Selectors';
import { createInitialDesignDataFromDesignAreas } from '@configuratorware/configurator-frontendgui/App/Reducers/DesignArea/Transformers';
import { get, isArray } from 'lodash';
import { patchViewImages } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Modifiers';
import { addImagesFromDesignArea } from '@configuratorware/configurator-frontendgui/App/Reducers/ImageGallery/Actions';
import { getConf } from '@configuratorware/configurator-frontendgui/App/configuration';
import {
  addCustomFonts,
  fetchFonts,
} from '@configuratorware/configurator-frontendgui/App/Reducers/Fonts/Actions';
import { getUsedCustomFontsInEditor } from '@configuratorware/configurator-frontendgui/App/Reducers/Fonts/Selectors';
import { setInitialDesignData } from '@configuratorware/configurator-frontendgui/App/Reducers/DesignData/Actions';
import { EventTypes } from '@configuratorware/configurator-frontendgui/App/Constant';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';

export const loadDesignerData = (configuration) => async (dispatch) => {
  if (!configuration || !configuration.item || !configuration.item.identifier) {
    return dispatch(reportError(t('loadConfigurationModes.error.missingItemIdentifier')));
  }
  dispatch(dismissError());
  dispatch(setWorkInProgress('loadConfigurationModes', true));

  try {
    return await dispatch(
      apiActions.postDataAsync('/configurationmodes/designer/getdata', { configuration }, receiveDesignerData)
    );
  } catch (e) {
    const state = Services.store.state;
    const { designArea } = state;

    await dispatch(
      apiActions.postData('/configurationmodes/designer/logloadingerror', {
        itemIdentifier: configuration?.item?.identifier,
        errorMessage: e.message,
        selectedDesignArea: designArea?.selectedDesignArea
          ? JSON.stringify(designArea.selectedDesignArea)
          : null,
      })
    );

    return dispatch(reportError(t('loadConfigurationModes.error.loadingFailed')));
  } finally {
    dispatch(setWorkInProgress('loadConfigurationModes', false));
  }
};

export const receiveConfiguration =
  (json, { checkValidation }) =>
  async (dispatch, getState) => {
    const prevState = getState();

    dispatch(setCalculationEnabled(false));

    if (checkValidation) {
      dispatch(inspectValidationResults(json));
    }

    const action = dispatch({
      type: RECEIVE_CONFIGURATION,
      data: patchViewImages(json),
      receivedAt: Date.now(),
    });

    dispatch(inspectCheckResults());

    const { configurator } = getState();
    const { configuration, selectedoptionclassification, loadIdentifier } = configurator;
    const { designer: designerEnabled } = getAvailableConfigurationModes(getState());

    if (configurator.loadCode) {
      dispatch(addImagesFromDesignArea(configurator));
    }

    dispatch(deleteMissingVariants(configurator));

    // load first optionclassification if not selected yet
    if (!selectedoptionclassification.identifier && getConf('showInitialOptionclassification')) {
      dispatch(loadInitialOptionclassification(configuration));
    }

    if (getConf('loadInspirations')) {
      dispatch(fetchInspirations(configuration.item.identifier));
    }

    dispatch(setVisible('configurationvariantSelection', false));

    if (
      prevState.configurator.configuration.item.identifier &&
      prevState.configurator.configuration.item.identifier !== configuration.item.identifier
    ) {
      dispatch(fetchConfigurationVariants(configuration.item.identifier));
    }

    applyVariantAmountPreSelection(configurator);

    setDefaultSelectedVariantIdentifier(getConfigurator(getState()), loadIdentifier);

    if (designerEnabled) {
      await dispatch(fetchFonts());
      dispatch(addCustomFonts(getUsedCustomFontsInEditor(getState())));
      await dispatch(loadDesignerData(getConfiguration(getConfigurator(getState()))));
      const initialDesignData = createInitialDesignDataFromDesignAreas(
        get(getState(), 'designArea.filteredDesignAreas') || [],
        getDesignData(getConfigurator(getState()))
      );
      dispatch(setInitialDesignData(initialDesignData));
    }

    // enable calculation and trigger it
    dispatch(setCalculationEnabled(true));
    dispatch(invalidateCalculation());
    dispatch(doCalculation(getConfiguration(getConfigurator(getState()))));
    return action;
  };

export const fetchConfigurationForDesignerProductPreview = () => async (dispatch, getState) => {
  const itemIdentifier = getState().configurator.loadIdentifier;

  dispatch(setCalculationEnabled(false));
  await dispatch(
    apiActions.fetchData('configuration/loadbyitemidentifier/' + itemIdentifier, (json) => ({
      type: RECEIVE_CONFIGURATION,
      data: patchViewImages(json),
      receivedAt: Date.now(),
    }))
  );
  setDefaultSelectedVariantIdentifier(getConfigurator(getState()), itemIdentifier);

  const { designer: designerEnabled } = getAvailableConfigurationModes(getState());

  if (designerEnabled) {
    dispatch(selectConfigurationMode('designer'));
    await dispatch(loadDesignerData(getConfiguration(getConfigurator(getState()))));
    const initialDesignData = createInitialDesignDataFromDesignAreas(
      get(getState(), 'designArea.filteredDesignAreas') || [],
      getDesignData(getConfigurator(getState()))
    );
    dispatch(setInitialDesignData(initialDesignData));
  }
};

export const fetchConfiguration = (itemidentifier) => {
  const selectOptions = getConf('selectOptions');
  let urlToCall = `configuration/loadbyitemidentifier/${itemidentifier}`;
  if (selectOptions) {
    const switchOptionData = JSON.stringify(getSwitchOptionData(selectOptions));
    urlToCall = `${urlToCall}?_switch_options=${switchOptionData}`;
  }

  return apiActions.fetchData(urlToCall, receiveConfiguration);
};

export const fetchConfigurationByCode = (code, checkValidation = true) => {
  return apiActions.fetchData('configuration/loadbyconfigurationcode/' + code, receiveConfiguration, {
    checkValidation,
  });
};

export const fetchConfigurationByCodeAsync = (code, checkValidation = true) => {
  return apiActions.fetchDataAsync('configuration/loadbyconfigurationcode/' + code, receiveConfiguration, {
    checkValidation,
  });
};

const getSwitchOptionData = (optionsList) => {
  const switchOptionData = {};
  for (const component of optionsList) {
    switchOptionData[component.identifier] = component.selectedoptions.map(
      ({ identifier, amount, inputText }) => ({
        identifier,
        inputText,
        amount: typeof amount === 'undefined' ? 1 : amount,
        check_results: null,
      })
    );
  }

  return switchOptionData;
};

export const fetchInitialConfiguration = () => (dispatch, getState) => {
  const { loadCode, loadIdentifier } = getState().configurator;
  const defaultLoadIdentifier = getConf('defaultLoadIdentifier');

  if (loadCode) {
    // Load the configuration by code
    return dispatch(fetchConfigurationByCode(loadCode)).then((configuration) => {
      if (configuration.error) {
        dispatch(reportConfigurationNotFoundError(t('loadConfiguration.error.loadingFailed')));
      } else {
        Services.analytics.triggerEvent(EventTypes.load_item, configuration.data.item.identifier);
        dispatch(setLoadCode(null));
      }
    });
  } else if (getConf('loadVariantsFirst')) {
    // Load variants by identifier
    dispatch(setVisible('configurationvariantSelection', true));

    return dispatch(fetchConfigurationVariants(loadIdentifier)).then((result) => {
      // if the result contains error just return, do nothing else
      if (result.error) {
        return;
      }

      const { configurationvariants, loadIdentifier } = getState().configurator;

      // If there is only one variant or the list is empty, fall back to fetchConfiguration
      if (isArray(configurationvariants)) {
        if (configurationvariants.length === 0) {
          // Load the options for the initially selected optionclassification
          dispatch(fetchConfiguration(loadIdentifier));
        }
        if (configurationvariants.length === 1) {
          // There is only one variant in the list, just load it
          dispatch(fetchConfiguration(configurationvariants[0].identifier));
        }
      }
    });
  } else if (loadIdentifier === '__empty__') {
    return Promise.resolve();
  } else if (loadIdentifier) {
    return dispatch(fetchConfiguration(loadIdentifier));
  } else if (defaultLoadIdentifier) {
    return dispatch(fetchConfiguration(defaultLoadIdentifier));
  }
};

export const loadConfiguration = () => async (dispatch, getState) => {
  const { loadCode } = getState().configurator;
  if (!loadCode) {
    throw new Error(t('loadConfiguration.error.emptyCode'));
  }
  dispatch(dismissError());
  dispatch(setWorkInProgress('loadConfiguration', true));

  try {
    const action = await dispatch(fetchConfigurationByCodeAsync(loadCode));
    dispatch(setVisible('loadConfiguration', false));
    return action;
  } catch (e) {
    dispatch(dismissError()); // ignore original REQUEST_ERROR, should be handled by the caller
    throw new Error(t('loadConfiguration.error.loadingFailed'));
  } finally {
    dispatch(setWorkInProgress('loadConfiguration', false));
  }
};
